<template>
  <div :id="module.id">
    <SsrJobLinks :links="jobLinks" />

    <ClientOnly>
      <template #fallback>
        <CRSearchLoadingPlaceholder :job-search-global="module" used-in-module />
      </template>

      <AisInstantSearch
        v-if="searchAvailable"
        :search-client="searchClient"
        :index-name="indexName"
        :insights="{
          insightsClient: null,
          onEvent: (event: any) => {
            handleTrackInsightsEvent(event);
          },

        }"
      >
        <CRSearchAndFilterModule
          :computed-function="computed"
          :module="module"
          :search-tablist-aria-label="searchTablistAriaLabel"
          :toggle-list-view-aria-label="searchToggleListViewAriaLabel"
          :save-filters-text="searchSaveFiltersText"
          :toggle-map-view-aria-label="searchToggleMapViewAriaLabel"
          @filter-clicked="handleTrackFilterOpened($event)"
          @view-changed="handleViewChanged"
        >
          <template #configure>
            <AisConfigure
              :hits-per-page.camel="10"
              :query="queryPreset"
              :filters="combinedFilters"
            />
          </template>

          <template #search-box>
            <AisSearchBox
              :placeholder="module.config.search.placeholderText"
              @input="handleTrackSearchInput"
            />
          </template>

          <template #filter-list="{ filter }">
            <CRListFilterWrapper :filter-key="filter.key">
              <AisRefinementList :attribute="filter.key" searchable :limit="1000" :sort-by="listSorting">
                <template
                  #default="{
                    items,
                    isFromSearch,
                    refine,
                    createURL,
                    searchForItems,
                    sendEvent,
                  }"
                >
                  <CREndlessFilterList
                    :attribute="filter.key"
                    :items="items"
                    :refine="refine"
                    :search-for-items="searchForItems"
                    :create-u-r-l="createURL"
                    :is-from-search="isFromSearch"
                    :send-event="sendEvent"
                    :reset-button-label="searchClearFilterSearchLabel"
                    :job-search-global="module"
                  />
                </template>
              </AisRefinementList>
            </CRListFilterWrapper>
          </template>

          <template #filter-location="{ filter }">
            <CRListFilterWrapper :filter-key="filter.key">
              <div>
                <CRLocationAutocomplete
                  :api-url="locationAutocompleteUrl"
                  :language="jobShopLocale.split('-')[0]"
                  :placeholder="module.config.filters.locationSearchText"
                  :clear-input="resetAutocomplete"
                  :clear-button-label="searchClearLocationLabel"
                  @address-chosen="handleFilterAddress"
                  @address-cleared="handleClearAddress"
                />
                <CRLocationRadiusSelector
                  :coordinates="aroundLatLng"
                  :initial-radius="aroundRadius"
                  :radius-options="[10000, 25000, 50000, 100000]"
                  :group-label="searchRadiusGroupLabel"
                  @radius-changed="handleChangeRadius"
                />
              </div>
              <AisRefinementList
                :attribute="filter.key"
                searchable
                :limit="1000"
                :sort-by="listSorting"
              >
                <template
                  #default="{
                    items,
                    isFromSearch,
                    refine,
                    createURL,
                    searchForItems,
                    sendEvent,
                  }"
                >
                  <CREndlessFilterList
                    :attribute="filter.key"
                    :items="items"
                    :refine="refine"
                    :search-for-items="searchForItems"
                    :create-u-r-l="createURL"
                    :is-from-search="isFromSearch"
                    :send-event="sendEvent"
                    :reset-button-label="searchClearFilterSearchLabel"
                    :job-search-global="jobSearchGlobal"
                    :hide-search="true"
                  />
                </template>
              </AisRefinementList>
            </CRListFilterWrapper>
          </template>

          <template #filter-select="{ filter }">
            <AisRefinementList
              :attribute="filter.key"
              :limit="1000"
              :class-names="{
                'ais-RefinementList': 'search-filter-filter-select-list',
              }"
              :sort-by="listSorting"
            >
              <template #default="{ items, refine }">
                <button
                  v-for="item in items.sort((a, b) => a.value < b.value)"
                  :key="item.value"
                  class="select-button"
                  :class="item.isRefined ? '-active' : ''"
                  @click.prevent="refine(item.value)"
                >
                  {{ item.value }}
                </button>
              </template>
            </AisRefinementList>
          </template>

          <template #filter-slider="{ filter }">
            <AisRangeInput :attribute="filter.key">
              <template #default="{ currentRefinement, range, refine }">
                <div :ref="`slider-${filter.key}`" class="slider-container">
                  <Slider
                    :min="range.min"
                    :max="range.max"
                    :model-value="toValue(currentRefinement, range)"
                    :tooltips="true"
                    tooltip-position="bottom"
                    :options="sliderHandleOptions(filter)"
                    @update="refineRange(refine, $event)"
                    @slide="
                      setSliderTooltipOrientation($event, range, filter.key)
                    "
                  />
                </div>
              </template>
            </AisRangeInput>
          </template>

          <template #single-filter-reset="{ filter }">
            <AisClearRefinements :included-attributes="[filter.key]">
              <template #resetLabel>
                {{ module.config.filters.resetButtonsText.singleFilter }}
              </template>
            </AisClearRefinements>
          </template>

          <template #results-loading>
            <AisStateResults>
              <template #default="{ status }">
                <CRResultsLoading
                  v-show="status === 'loading'"
                  :job-search-global="module"
                />
              </template>
            </AisStateResults>
          </template>

          <template #stats>
            <AisStats>
              <template #default="{ nbHits }">
                <!-- eslint-disable vue/no-unused-refs -->
                <CRSearchStats ref="searchStats" :number-of-hits="nbHits" :stats-text="module.config.results.amountHitsText" :total-count="totalOffersCount" />
                <!-- eslint-enable vue/no-unused-refs -->
              </template>
            </AisStats>
          </template>

          <template #sorting-select>
            <AisSortBy :items="module.config.sorting.items" />
          </template>

          <template #all-filter-reset>
            <AisClearRefinements>
              <template #default="{ canRefine, refine }">
                <CRResetFiltersButton
                  :label="
                    module.config.filters.resetButtonsText.allFilters
                  "
                  :disabled="!canRefine && !(aroundRadius && aroundLatLng)"
                  @click.prevent="handleClickRemoveAllFilter(refine)"
                  @mousedown="handleTrackClickRemoveAllFilter"
                />
              </template>
            </AisClearRefinements>
          </template>

          <template #results>
            <AisStateResults>
              <template #default="{ results: { hits } }">
                <AisHits>
                  <template #item="{ item }">
                    <CRResultItem :item="item" :job-search-global="module" :more-locations-text="moreLocationsText" />
                  </template>
                </AisHits>

                <AisPagination>
                  <template
                    #default="{
                      currentRefinement,
                      nbPages,
                      isFirstPage,
                      isLastPage,
                      refine,
                    }"
                  >
                    <CRResultsPagination
                      v-show="hits.length"
                      :pagination-config="module.config.results.pagination"
                      :current-page="currentRefinement"
                      :number-of-pages="nbPages"
                      :is-first-page="isFirstPage"
                      :is-last-page="isLastPage"
                      :change-page-function="refine"
                      scroll-target=".search-module-section"
                      @page-changed="handleTrackPaginationChange($event)"
                    />
                  </template>
                </AisPagination>
              </template>
            </AisStateResults>
          </template>

          <template #empty-state>
            <AisStateResults>
              <template #default="{ results: { hits } }">
                <CRResultsEmptyState
                  v-show="hits.length === 0"
                  :headline="module.config.results.emptyState.headline"
                  :subline="module.config.results.emptyState.subline"
                />
              </template>
            </AisStateResults>
          </template>

          <template #map-view>
            <AisConfigure :hits-per-page.camel="250" />
            <CRSearchMapContainer
              :job-search-global="module"
              :tiles-url="mapTilesUrl"
              :cdn-url="cdnUrl"
              :map-language="jobShopLocale.split('-')[0]"
            >
              <CRSearchMapVue3
                @marker-clicked="handleTrackMapMarkerClicked"
                @slide-changed="handleTrackMapMarkerSlideChanged"
              />
            </CRSearchMapContainer>
          </template>
        </CRSearchAndFilterModule>
      </AisInstantSearch>

      <div v-else class="search-unavailable">
        {{ searchUnavailableText }}
      </div>
    </ClientOnly>
  </div>
</template>

<script lang="ts">
import {
  AisClearRefinements,
  AisConfigure,
  AisHits,
  AisInstantSearch,
  AisPagination,
  AisRangeInput,
  AisRefinementList,
  AisSearchBox,
  AisSortBy,
  AisStateResults,
  AisStats,
} from "vue-instantsearch/vue3/es"
import Slider from "@vueform/slider"
import { searchAndFilterMethods } from "@jobshop/crossroads/components/SearchAndFilter/mixins/searchAndFilter"
import { searchAndFilterModuleComputed } from "@jobshop/crossroads/components/SearchAndFilter/mixins/searchAndFilterModule"
import { debounce } from "radash"
import { useTypesenseSearchClient } from "~/composables/useTypesenseSearchClient"
import { InsightsEvents } from "~/utils/enums/InsightsEvents"

const indexName = "offers"

export default defineNuxtComponent({
  components: {
    AisClearRefinements,
    AisHits,
    AisInstantSearch,
    AisRangeInput,
    AisRefinementList,
    AisSearchBox,
    AisSortBy,
    AisStateResults,
    AisStats,
    AisPagination,
    Slider,
    AisConfigure,
  },
  mixins: [searchAndFilterMethods, searchAndFilterModuleComputed],
  props: {
    module: {
      type: Object as PropType<ContentModule>,
      required: true,
    },
  },
  data() {
    return {
      totalOffersCount: 0,
      filtersPreset: "",
      queryPreset: "",
      currentAddress: "", // for sending to tracking
    }
  },
  async setup(props) {
    const { t } = useI18n()
    const { data: jobShop } = useNuxtData("jobShopData")

    // Resolve instance-dependent deps before any await — hooks relying on
    // getCurrentInstance() must not be called after an await in async setup()
    // (see AGENTS.md).
    const nuxtApp = useNuxtApp()
    const { tenantId, jobShopId } = useHeaderStore()
    const { public: { cdnUrl, mapTilesUrl, locationAutocompleteUrl, typesenseUrl } } = useRuntimeConfig()

    // Fetch typesense API key with target job shops
    const targetJobShops = props.module.config.general.targetJobShops
    const { typesenseApiKey, refreshTypesenseApiKey } = await useTypesenseApiKey(targetJobShops)
    // Search is available only when the scoped key resolved (offer-service reachable);
    // flipped to false if a search later fails at runtime.
    const searchAvailable = ref(!!typesenseApiKey.value)

    // Get typesense search client (skip entirely when there is no key)
    let searchClient = null

    if (searchAvailable.value) {
      const client = await useTypesenseSearchClient(props.module.config, typesenseApiKey.value, refreshTypesenseApiKey)
      searchClient = client.searchClient
    }

    // Crawlable (no-JS) job links rendered into a <noscript> block — see SsrJobLinks.vue.
    // Passes the module's preset query + filters (incl. the nationwide-location
    // augmentation the live search applies) so the links match the offers the
    // module actually displays. (JET-1826)
    const { jobLinks } = await useSsrJobLinks(
      typesenseApiKey.value,
      { api: nuxtApp.$api, tenantId, jobShopId, typesenseUrl },
      {
        scope: props.module.id,
        query: props.module.config.search.preset,
        filters: resolveModuleFilterBy(props.module.config),
      },
    )

    // Provide locale & cdn urls for map
    const jobShopLocale = computed(() => jobShop.value?.locale)

    // Provide translations
    const searchTablistAriaLabel = computed(() => t("search.tablist-aria-label", 0, { locale: jobShopLocale.value }))
    const searchToggleMapViewAriaLabel = computed(() => t("search.toggle-map-view-aria-label", 0, { locale: jobShopLocale.value }))
    const searchToggleListViewAriaLabel = computed(() => t("search.toggle-list-view-aria-label", 0, { locale: jobShopLocale.value }))
    const searchSaveFiltersText = computed(() => t("search.save-filters", 0, { locale: jobShopLocale.value }))
    const searchClearLocationLabel = computed(() => t("search.clear-location", 0, { locale: jobShopLocale.value }))
    const searchRadiusGroupLabel = computed(() => t("search.radius-group", 0, { locale: jobShopLocale.value }))
    const searchClearFilterSearchLabel = computed(() => t("search.clear-filter-search", 0, { locale: jobShopLocale.value }))
    const sliderHandleOptionsCache = new Map<string, object>()
    const sliderHandleOptions = (filter: { label?: string }) => {
      const locale = jobShopLocale.value
      const cacheKey = `${locale}|${filter.label ?? ""}`
      if (!sliderHandleOptionsCache.has(cacheKey)) {
        sliderHandleOptionsCache.set(cacheKey, {
          handleAttributes: [
            { "aria-label": filter.label ? t("search.slider-min-of", { label: filter.label }, { locale }) : t("search.slider-min", 0, { locale }) },
            { "aria-label": filter.label ? t("search.slider-max-of", { label: filter.label }, { locale }) : t("search.slider-max", 0, { locale }) },
          ],
        })
      }
      return sliderHandleOptionsCache.get(cacheKey)
    }
    const searchUnavailableText = computed(() => t("search.unavailable", 0, { locale: jobShopLocale.value }))
    const { moreLocationsText } = useMoreLocationsText(jobShopLocale, t)

    // Track loaded filters
    // Only possible on client, since amplitude is a client-side plugin
    if (import.meta.client) {
      props.module.config.filters.options.forEach((filter: FilterOption) => {
        if (filter.active)
          trackFilterLoaded(filter.key)
      })
    }

    // Use ref on CRSearchStats to track number of results
    const searchStats = ref(null)
    watchEffect(() => {
      if (searchStats.value)
        trackLoadSearchResults(searchStats.value.numberOfHits)
    })

    // Define sorting for list and select filters
    const listSorting = ref(["isRefined", "name:asc"])

    return {
      searchClient,
      jobLinks,
      jobShopLocale,
      cdnUrl,
      mapTilesUrl,
      searchStats,
      indexName,
      listSorting,
      locationAutocompleteUrl,
      searchTablistAriaLabel,
      searchToggleMapViewAriaLabel,
      searchToggleListViewAriaLabel,
      searchSaveFiltersText,
      searchClearLocationLabel,
      searchRadiusGroupLabel,
      searchClearFilterSearchLabel,
      sliderHandleOptions,
      moreLocationsText,
      searchAvailable,
      searchUnavailableText,
    }
  },
  async mounted() {
    await this.fetchTotalOffersCount()
    this.setPresets()
  },
  methods: {
    setPresets() {
      this.queryPreset = this.module.config.search.preset
      this.filtersPreset = this.resolvedPresetFilters
    },
    async fetchTotalOffersCount() {
      if (!this.searchAvailable || !this.searchClient)
        return

      try {
        const response = await this.searchClient.search([
          {
            indexName: "offers",
            params: {
              query: this.module.config.search.preset,
              filters: this.resolvedPresetFilters,
            },
          },
        ])

        this.totalOffersCount = response.results[0].nbHits
      }
      catch {
        this.searchAvailable = false
      }
    },
    handleTrackSearchInput: debounce({ delay: 1000 }, (event) => {
      const searchRequest = event.target.value

      if (searchRequest)
        trackSearchApplied(searchRequest)
    }),
    handleTrackFilterOpened(event: string) {
      trackFilterOpened(event)
    },
    handleTrackClickRemoveAllFilter() {
      trackClickRemoveAllFilter()
    },
    handleClickRemoveAllFilter(refine) {
      this.resetAllFilters(refine)
      this.currentAddress = ""
    },
    handleViewChanged(event: any) {
      this.updateSearchParameters(event)
      trackSearchViewChanged(event)
    },
    handleTrackMapMarkerClicked() {
      trackClickMapMarker()
    },
    handleTrackMapMarkerSlideChanged(event: any) {
      trackMapMarkerSlideChanged(event)
    },
    handleTrackViewSearchResult(item: any, itemIndex: number) {
      trackViewSearchResult(item.id, item.title, itemIndex + 1)
    },
    handleTrackInsightsEvent(event: any) {
      switch (event.insightsMethod) {
        case InsightsEvents.CLICKED_FILTERS:
          trackFilterSelected(event.attribute, event.payload.filters[0].split(":")[1])
          break
        case InsightsEvents.CLICKED_RESULTS:
          trackClickSearchResult(event.hits[0].id, event.hits[0].title, event.payload.positions[0])
          break
      }
    },
    handleTrackPaginationChange(changeType: string) {
      trackSearchResultsPageChanged(changeType)
    },
    handleFilterAddress(address) {
      this.currentAddress = address
      this.filterAddress(address)

      if (this.currentAddress && this.aroundRadius)
        trackLocationRadiusFilterSelected(this.currentAddress.location.city, this.aroundRadius / 1000)
    },
    handleChangeRadius(km) {
      this.changeRadius(km)

      if (this.currentAddress && this.aroundRadius)
        trackLocationRadiusFilterSelected(this.currentAddress.location.city, this.aroundRadius / 1000)
    },
    handleClearAddress() {
      this.currentAddress = ""
      this.clearAddress()
    },
  },
})
</script>

<style src="@vueform/slider/themes/default.css"></style>

<style src="@jobshop/crossroads/assets/scss/search-filter/_index.scss"></style>

<style lang="scss" scoped>
.search-unavailable {
  @include font-family("body");
  @include scale-fonts("body");

  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 240px;
  padding: var(--cr-content-padding-m, 24px);

  color: var(--search-filter-text-secondary-color, #5e5e5e);
  text-align: center;
  text-wrap: balance;
}
</style>
